import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { apiList, apiCreate } from '../../api/apiClient';
import { getPDKLiveDevices } from '../../api/pdkApi';
import { authorizeSchlage, listSchlageDevices, SchlageLock } from '../../api/schlageApi';

interface Props {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  existingDevices: any[];
  onAdded: () => void;
}

type Mode = 'choose' | 'acs' | 'manual' | 'schlage';

export const AddDeviceModal: React.FC<Props> = ({ open, onClose, propertyId, existingDevices, onAdded }) => {
  const [mode, setMode] = useState<Mode>('choose');
  const [systems, setSystems] = useState<any[]>([]);
  const [systemsLoading, setSystemsLoading] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState<any>(null);
  const [acsDevices, setAcsDevices] = useState<any[]>([]);
  const [acsLoading, setAcsLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  // Schlage flow
  const [schlageStep, setSchlageStep] = useState<'intro' | 'authorizing' | 'pick' | 'saving'>('intro');
  const [schlageAcsSystemId, setSchlageAcsSystemId] = useState<string | null>(null);
  const [schlageLocks, setSchlageLocks] = useState<SchlageLock[]>([]);
  const [schlageSelected, setSchlageSelected] = useState<Set<string>>(new Set());

  // Manual form
  const [manualName, setManualName] = useState('');
  const [manualType, setManualType] = useState('lock');
  const [manualSerial, setManualSerial] = useState('');
  const [manualModel, setManualModel] = useState('');
  const [manualLoading, setManualLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setMode('choose'); setSelectedSystem(null); setAcsDevices([]); setSelected(new Set()); setError('');
      setSchlageStep('intro'); setSchlageAcsSystemId(null); setSchlageLocks([]); setSchlageSelected(new Set());
    }
  }, [open]);

  useEffect(() => {
    if (open && mode === 'acs') loadSystems();
  }, [open, mode]);

  const loadSystems = async () => {
    setSystemsLoading(true);
    try {
      const rows = await apiList('access_control_systems', { property_id: propertyId });
      // Schlage acs_systems rows back individual locks — hide them here so they
      // only appear on the Devices list, not as importable ACS systems.
      setSystems(Array.isArray(rows) ? rows.filter((s: any) => String(s.provider || '').toLowerCase() !== 'schlage') : []);
    } catch { setSystems([]); }
    finally { setSystemsLoading(false); }
  };

  const selectSystem = async (system: any) => {
    setSelectedSystem(system);
    setAcsDevices([]);
    setSelected(new Set());
    setAcsLoading(true);
    try {
      const existingIds = new Set(existingDevices.map((d: any) => String(d.acs_device_id || d.integration_device_id)).filter(Boolean));
      if (system.provider === 'pdk') {
        const pdkSystemId = system.system_id || system.pdk_system_id;
        const live = await getPDKLiveDevices(pdkSystemId);
        const devs = (Array.isArray(live) ? live : []).map((d: any) => ({
          id: String(d.id),
          name: d.name || `Device ${d.id}`,
          type: d.type || 'lock',
          online: d.connection_status === 'online',
          already_added: existingIds.has(String(d.id)),
        }));
        setAcsDevices(devs);
        setSelected(new Set(devs.filter(d => !d.already_added).map(d => d.id)));
      } else {
        setAcsDevices([]);
      }
    } catch (e: any) {
      setError('Failed to load devices: ' + (e?.message || 'Unknown error'));
    } finally {
      setAcsLoading(false);
    }
  };

  const toggleDevice = (id: string) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const handleAddFromAcs = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    setError('');
    try {
      for (const devId of Array.from(selected)) {
        const dev = acsDevices.find(d => d.id === devId);
        if (!dev) continue;
        await apiCreate('devices', {
          name: dev.name,
          device_type: dev.type || 'lock',
          property_id: propertyId,
          status: 'active',
          serial_number: `${selectedSystem?.provider?.toUpperCase() || 'ACS'}-${dev.id}`,
          connection_status: dev.online ? 'online' : 'offline',
          acs_provider: selectedSystem?.provider || 'pdk',
          acs_device_id: dev.id,
          manufacturer: selectedSystem?.provider === 'pdk' ? 'ProDataKey' : selectedSystem?.provider,
        });
      }
      onAdded();
      onClose();
    } catch (e: any) {
      setError('Failed to add devices: ' + (e?.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const startSchlage = async () => {
    setError('');
    setSchlageStep('authorizing');
    try {
      const id = await authorizeSchlage(propertyId);
      setSchlageAcsSystemId(id);
      const locks = await listSchlageDevices(id);
      const existingIds = new Set(existingDevices.map((d: any) => String(d.acs_device_id)).filter(Boolean));
      const fresh = locks.filter((l) => !existingIds.has(String(l.external_id)));
      setSchlageLocks(fresh);
      setSchlageSelected(new Set(fresh.map((l) => l.external_id)));
      setSchlageStep('pick');
    } catch (e: any) {
      setError(e?.message || 'Schlage authorization failed');
      setSchlageStep('intro');
    }
  };

  const toggleSchlageLock = (id: string) => {
    const s = new Set(schlageSelected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSchlageSelected(s);
  };

  const handleAddSchlage = async () => {
    if (!schlageAcsSystemId || schlageSelected.size === 0) return;
    setSchlageStep('saving');
    setError('');
    try {
      for (const extId of Array.from(schlageSelected)) {
        const lock = schlageLocks.find((l) => l.external_id === extId);
        if (!lock) continue;
        await apiCreate('devices', {
          name: lock.name || `Schlage Lock ${extId.slice(0, 6)}`,
          device_type: 'lock',
          property_id: propertyId,
          status: 'active',
          serial_number: lock.serial_number || `SCHLAGE-${extId}`,
          model: lock.model,
          manufacturer: 'Schlage',
          connection_status: lock.connected ? 'online' : 'offline',
          acs_provider: 'schlage',
          acs_device_id: extId,
          // Residential integrations are backed by smart_lock_accounts, not
          // acs_systems. Persist both canonical smart-lock identifiers so the
          // device can route lock/PIN/history operations to that account.
          smart_lock_provider: 'schlage',
          smart_lock_device_id: extId,
          smart_lock_system_id: schlageAcsSystemId,
          battery_percent: lock.battery_percent,
          battery_state: lock.battery_state,
          provider_last_synced_at: new Date().toISOString(),
        });
      }
      onAdded();
      onClose();
    } catch (e: any) {
      setError('Failed to add Schlage locks: ' + (e?.message || 'Unknown error'));
      setSchlageStep('pick');
    }
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualName.trim()) { setError('Name is required'); return; }
    setManualLoading(true);
    setError('');
    try {
      await apiCreate('devices', {
        name: manualName.trim(),
        device_type: manualType || 'lock',
        property_id: propertyId,
        status: 'active',
        serial_number: manualSerial || undefined,
        model: manualModel || undefined,
      });
      onAdded();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to create device');
    } finally {
      setManualLoading(false);
    }
  };

  const notAdded = acsDevices.filter(d => !d.already_added);

  return (
    <Modal open={open} onClose={onClose} title="Add Device" size="lg">
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

      {mode === 'choose' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-500 mb-4">Choose how to add a device to this property.</p>
          <button
            className="w-full text-left p-4 border-2 border-gray-200 rounded-xl hover:border-brand-400 hover:bg-brand-50 transition-colors"
            onClick={() => setMode('acs')}
          >
            <div className="font-semibold text-gray-900">From ACS System</div>
            <div className="text-sm text-gray-500 mt-0.5">Import devices from a connected access control system (PDK, Brivo, etc.)</div>
          </button>
          <button
            className="w-full text-left p-4 border-2 border-gray-200 rounded-xl hover:border-brand-400 hover:bg-brand-50 transition-colors"
            onClick={() => setMode('schlage')}
          >
            <div className="font-semibold text-gray-900">Schlage Smart Lock</div>
            <div className="text-sm text-gray-500 mt-0.5">Connect your Schlage Home account and pick a lock to install at this property.</div>
          </button>
          <button
            className="w-full text-left p-4 border-2 border-gray-200 rounded-xl hover:border-brand-400 hover:bg-brand-50 transition-colors"
            onClick={() => setMode('manual')}
          >
            <div className="font-semibold text-gray-900">Add Manually</div>
            <div className="text-sm text-gray-500 mt-0.5">Enter device details directly (for standalone or non-ACS devices)</div>
          </button>
        </div>
      )}

      {mode === 'schlage' && (
        <div>
          <button className="text-sm text-brand-600 hover:underline mb-4" onClick={() => setMode('choose')}>← Back</button>
          {schlageStep === 'intro' && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                <p className="font-semibold mb-1">Compatibility</p>
                <p>Intrlock connects to Schlage locks through Schlage's cloud API. Your lock must be Wi-Fi connected to the Schlage Home cloud before it can be added here.</p>
                <ul className="list-disc list-inside mt-2 space-y-0.5 text-xs">
                  <li><strong>Schlage Encode</strong> (BE489) and <strong>Encode Plus</strong> (BE499) — built-in Wi-Fi, supported out of the box once paired via the Schlage Home app's Wi-Fi setup (not HomeKit-only).</li>
                  <li><strong>Schlage Sense</strong> (BE479) — Bluetooth-only; requires a <strong>Schlage Sense Wi-Fi Adapter</strong> (BR400) plugged in near the lock.</li>
                  <li>Locks paired only via Apple HomeKit (without Schlage Wi-Fi setup) and Z-Wave-only models (BE469) are not visible to the Schlage API.</li>
                </ul>
              </div>
              <p className="text-sm text-gray-600">
                Click below to sign in with your Schlage Home account. A popup will open at account.schlage.com.
                Once approved, we'll list every cloud-connected lock on your account and you can pick which ones to install here.
              </p>
              <Button onClick={startSchlage}>Connect Schlage Home</Button>
            </div>
          )}
          {schlageStep === 'authorizing' && (
            <p className="text-sm text-gray-500 text-center py-8">Waiting for Schlage authorization… complete the popup, then this will continue automatically.</p>
          )}
          {schlageStep === 'pick' && (
            <>
              <p className="text-sm font-medium text-gray-700 mb-3">Pick locks to add to this property</p>
              {schlageLocks.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">No new Schlage locks found on this account (or all are already added).</p>
              )}
              {schlageLocks.length > 0 && (
                <div className="max-h-56 overflow-y-auto space-y-1 border border-gray-100 rounded-lg p-2">
                  {schlageLocks.map((l) => (
                    <label key={l.external_id} className="flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={schlageSelected.has(l.external_id)}
                        onChange={() => toggleSchlageLock(l.external_id)}
                        className="rounded text-brand-600"
                      />
                      <span className="text-sm font-medium text-gray-900 flex-1">{l.name || `Lock ${l.external_id.slice(0,8)}`}</span>
                      {l.model && <Badge color="gray">{l.model}</Badge>}
                      <span className={`w-2 h-2 rounded-full ${l.connected ? 'bg-green-500' : 'bg-red-400'}`} title={l.connected ? 'Online' : 'Offline'} />
                    </label>
                  ))}
                </div>
              )}
              <div className="flex gap-2 justify-end mt-4">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleAddSchlage} disabled={schlageSelected.size === 0}>
                  Add {schlageSelected.size} Lock{schlageSelected.size !== 1 ? 's' : ''}
                </Button>
              </div>
            </>
          )}
          {schlageStep === 'saving' && (
            <p className="text-sm text-gray-500 text-center py-8">Adding locks…</p>
          )}
        </div>
      )}

      {mode === 'acs' && !selectedSystem && (
        <div>
          <button className="text-sm text-brand-600 hover:underline mb-4" onClick={() => setMode('choose')}>← Back</button>
          <p className="text-sm text-gray-600 mb-4">Select a connected access control system to import devices from.</p>
          {systemsLoading && <p className="text-sm text-gray-400 text-center py-6">Loading systems…</p>}
          {!systemsLoading && systems.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">No ACS systems connected to this property.</p>
              <p className="text-xs mt-1">Connect a system first via the System page.</p>
            </div>
          )}
          <div className="space-y-2">
            {systems.map((s: any) => (
              <button key={s.id}
                className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-brand-400 hover:bg-brand-50 transition-colors flex items-center justify-between"
                onClick={() => selectSystem(s)}
              >
                <div>
                  <span className="font-medium text-gray-900">{s.name}</span>
                  <span className="text-xs text-gray-400 ml-2">{s.provider}</span>
                </div>
                <Badge color={s.status === 'active' ? 'green' : 'gray'}>{s.status || 'unknown'}</Badge>
              </button>
            ))}
          </div>
        </div>
      )}

      {mode === 'acs' && selectedSystem && (
        <div>
          <button className="text-sm text-brand-600 hover:underline mb-4" onClick={() => setSelectedSystem(null)}>← Back to systems</button>
          <p className="text-sm font-medium text-gray-700 mb-3">Devices on <span className="text-brand-700">{selectedSystem.name}</span></p>

          {acsLoading && <p className="text-sm text-gray-400 text-center py-6">Fetching devices…</p>}

          {!acsLoading && selectedSystem.provider !== 'pdk' && (
            <p className="text-sm text-gray-400 text-center py-6">Device import not yet supported for {selectedSystem.provider}.</p>
          )}

          {!acsLoading && selectedSystem.provider === 'pdk' && acsDevices.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">No devices found on this system.</p>
          )}

          {!acsLoading && acsDevices.length > 0 && (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                <div className="bg-green-50 rounded-lg p-2">
                  <p className="text-xl font-bold text-green-700">{acsDevices.filter(d => d.already_added).length}</p>
                  <p className="text-xs text-green-600">Already added</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-2">
                  <p className="text-xl font-bold text-amber-700">{notAdded.length}</p>
                  <p className="text-xs text-amber-600">Available</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-xl font-bold text-gray-700">{selected.size}</p>
                  <p className="text-xs text-gray-500">Selected</p>
                </div>
              </div>

              {notAdded.length > 0 && (
                <div className="flex justify-end mb-2">
                  <button className="text-xs text-brand-600 hover:underline font-medium" onClick={() => {
                    setSelected(selected.size === notAdded.length ? new Set() : new Set(notAdded.map(d => d.id)));
                  }}>
                    {selected.size === notAdded.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              )}

              <div className="max-h-56 overflow-y-auto space-y-1 border border-gray-100 rounded-lg p-2">
                {acsDevices.map(d => (
                  <label key={d.id} className={`flex items-center gap-3 p-2 rounded cursor-pointer ${d.already_added ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}>
                    <input
                      type="checkbox"
                      disabled={d.already_added}
                      checked={d.already_added || selected.has(d.id)}
                      onChange={() => !d.already_added && toggleDevice(d.id)}
                      className="rounded text-brand-600"
                    />
                    <span className="text-sm font-medium text-gray-900 flex-1">{d.name}</span>
                    <Badge color="gray">{d.type}</Badge>
                    <span className={`w-2 h-2 rounded-full ${d.online ? 'bg-green-500' : 'bg-red-400'}`} title={d.online ? 'Online' : 'Offline'} />
                    {d.already_added && <span className="text-xs text-gray-400">Added</span>}
                  </label>
                ))}
              </div>

              <div className="flex gap-2 justify-end mt-4">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleAddFromAcs} loading={saving} disabled={selected.size === 0}>
                  Add {selected.size} Device{selected.size !== 1 ? 's' : ''}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {mode === 'manual' && (
        <form onSubmit={handleManualSubmit}>
          <button type="button" className="text-sm text-brand-600 hover:underline mb-4" onClick={() => setMode('choose')}>← Back</button>
          <Input label="Name *" value={manualName} onChange={e => setManualName(e.target.value)} required />
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Device Type</label>
            <select
              value={manualType}
              onChange={e => setManualType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="lock">Lock</option>
              <option value="intercom">Intercom</option>
              <option value="camera">Camera</option>
              <option value="reader">Reader</option>
              <option value="gate">Gate</option>
              <option value="relay">Relay</option>
              <option value="sensor">Sensor</option>
              <option value="other">Other</option>
            </select>
          </div>
          <Input label="Serial Number" value={manualSerial} onChange={e => setManualSerial(e.target.value)} />
          <Input label="Model" value={manualModel} onChange={e => setManualModel(e.target.value)} />
          <div className="flex gap-3 justify-end mt-6">
            <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={manualLoading}>Create Device</Button>
          </div>
        </form>
      )}
    </Modal>
  );
};
